import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthUser } from '@builder/shared';
// Wave chat-identity-fix · 2026-05-26 — audit inline no setUser/setSession
// pra impedir que o store fique com `user.id` diferente do `JWT.sub`.
// O decode é puro (sem dependência de store) então não cria ciclo.
import { decodeJwtPayloadUnsafe } from '@/lib/jwt-decode';

interface AuthState {
  token: string | null;
  expiresAt: string | null;
  user: AuthUser | null;
  setSession: (session: { token: string; expiresAt: string; user: AuthUser }) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
}

/**
 * Verifica se `user.id` casa com `JWT.sub` do token atual. Se divergir,
 * a sessão está inconsistente — qualquer comparação "é meu?" no app vai
 * renderizar errado. Logs warning + devolve `false` pra caller decidir
 * (no nosso caso, abortar o setUser e limpar a sessão).
 *
 * Devolve `true` quando casa OU quando não dá pra decidir (token null,
 * JWT mal-formado, user null) — fail-OPEN porque bloquear logout/login
 * legítimos seria pior que tolerar um audit cego.
 */
function identityMatches(token: string | null, userId: string | null): boolean {
  if (!token || !userId) return true; // sem dados pra comparar
  const sub = decodeJwtPayloadUnsafe(token)?.sub;
  if (!sub) return true; // JWT mal-formado — não bloqueia
  if (sub === userId) return true;
  // eslint-disable-next-line no-console
  console.warn(
    '[auth.store] identity mismatch — refusing to persist inconsistent state. ' +
      `userId=${userId} jwtSub=${sub}`,
  );
  return false;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      expiresAt: null,
      user: null,
      setSession: (session) => {
        // Em setSession assume-se que token e user vieram da MESMA
        // resposta do backend — se não casarem, é bug crítico no backend.
        // Logamos mas aceitamos (não dá pra fazer melhor no client).
        identityMatches(session.token, session.user.id);
        set({ token: session.token, expiresAt: session.expiresAt, user: session.user });
      },
      setUser: (user) => {
        // Wave chat-identity-fix · este é O ponto crítico do bug do JP.
        // Vários endpoints (markWelcomed, updateProfile, playgroundExit)
        // atualizam user sem trocar token. Se o user.id que chegou
        // diverge do JWT.sub atual, NÃO persiste — limpa sessão pra
        // forçar re-login limpo.
        const token = get().token;
        if (!identityMatches(token, user.id)) {
          set({ token: null, expiresAt: null, user: null });
          return;
        }
        set({ user });
      },
      clear: () => set({ token: null, expiresAt: null, user: null }),
    }),
    {
      name: 'builder.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ token: s.token, expiresAt: s.expiresAt, user: s.user }),
      // Wave chat-identity-fix · audit em REHIDRATAÇÃO (boot do app no
      // mobile, F5 no desktop). Cobre o cenário de bug do JP onde
      // localStorage tinha user.id de uma sessão antiga.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (!identityMatches(state.token, state.user?.id ?? null)) {
          state.token = null;
          state.expiresAt = null;
          state.user = null;
        }
      },
    },
  ),
);

export function isSessionValid(state: AuthState): boolean {
  if (!state.token || !state.expiresAt) return false;
  return new Date(state.expiresAt).getTime() > Date.now();
}
